import { TextEditorContent } from "@/components/basic";
import SchedulesGroups from "@/components/map/SchedulesGroups";
import { useDateFormat } from "@/hooks/useDateFormat";
import { isRBFeature } from "@/lib/featureFlags";
import { AuthenticatedUser, IResource } from "@gettactic/api";
import { dateParseISO } from "@gettactic/helpers";
import {
  type IResourceWithSchedules,
  useAuthenticated
} from "@gettactic/hooks";
import { useSettings } from "@gettactic/hooks";
import { Spoiler } from "@mantine/core";
import { CapacityLabel } from "@v2/components/labels/CapacityLabel/CapacityLabel";
import React from "react";

const classes = {
  StatusBadge:
    "inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 font-medium text-gray-900 text-xs ring-1 ring-gray-200 ring-inset",
  StatusBadgeAvailable: "h-1.5 w-1.5 fill-green-500",
  StatusBadgeNotAvailable: "h-1.5 w-1.5 fill-red-500"
};

export interface ResourcesDetailsOverviewProps {
  resourceWithSchedules: IResourceWithSchedules;
  floor?: Partial<IResource> | null;
  formattedDate?: string;
  showAvailabilityDetails?: boolean;
  highlightUserId?: string | undefined;
}

const handleUnavailableDeskDetails = (
  authenticatedUser: AuthenticatedUser,
  resource: IResource
) => {
  if (!isRBFeature(authenticatedUser)) {
    if (resource.is_available) {
      return (
        <span className={classes.StatusBadge}>
          <svg
            className={classes.StatusBadgeAvailable}
            viewBox="0 0 6 6"
            aria-hidden="true"
          >
            <circle cx={3} cy={3} r={3} />
          </svg>
          Available
        </span>
      );
    }
    return (
      <span className={classes.StatusBadge}>
        <svg
          className={classes.StatusBadgeNotAvailable}
          viewBox="0 0 6 6"
          aria-hidden="true"
        >
          <circle cx={3} cy={3} r={3} />
        </svg>
        Not Available
      </span>
    );
  }
  if (resource.is_available) {
    return (
      <span className={classes.StatusBadge}>
        <svg
          className={classes.StatusBadgeAvailable}
          viewBox="0 0 6 6"
          aria-hidden="true"
        >
          <circle cx={3} cy={3} r={3} />
        </svg>
        Available
      </span>
    );
  }
  return undefined;
};

const handleDeskCapacity = (
  authenticatedUser: AuthenticatedUser,
  resource: IResource
) => {
  return isRBFeature(authenticatedUser) && resource.total_slots <= 1;
};

/**
 * For RB it will only display the 'Assigned' tag if the resource has available spots, and it is assigned to team or
 * users but the current user is not part of that group.
 */
const handleDeskAssigned = (
  authenticatedUser: AuthenticatedUser,
  resource: IResourceWithSchedules
) => {
  return !(
    isRBFeature(authenticatedUser) &&
    ((resource?.schedules?.length ?? 0) === resource.total_slots ||
      resource.available_slots !== 0)
  );
};

export const ResourcesDetailsOverview: React.FC<
  ResourcesDetailsOverviewProps
> = ({
  resourceWithSchedules,
  floor,
  showAvailabilityDetails = true,
  formattedDate: controlledFormattedDate,
  highlightUserId
  // isOnReservationModal = false
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { isOrgPrivacyEnabled } = useSettings();

  if (!resourceWithSchedules) {
    return null;
  }

  const formatDate = useDateFormat();

  const resource: IResource = resourceWithSchedules;
  const schedules = resourceWithSchedules.schedules;
  const formattedDate =
    controlledFormattedDate ||
    (schedules?.[0]?.start
      ? formatDate(dateParseISO(schedules?.[0]?.start))
      : "");

  const amenities = React.useMemo(() => {
    return resource.tags?.filter((tag) => tag.type === "amenity") || [];
  }, [resource.tags]);

  return (
    <div className="relative flex flex-col justify-items-center">
      <div className="flex">
        <div className="text-gray-800">
          <h2 className="font-bold text-xl">{resource.name ?? "N/A"}</h2>
          {floor ? (
            <span className="flex w-content items-center gap-1 font-inter text-[13px] text-muted-foreground">
              {floor.name}
              <CapacityLabel
                resource={resource}
                prefix={<span className="shrink-0">·</span>}
              />
            </span>
          ) : null}
          <div>
            {schedules?.length ? (
              <div className="mt-2">
                {formattedDate && (
                  <h5 className="font-bold text-secondary text-sm [.tactic-v2_&]:text-secondary-foreground">
                    Schedules for {formattedDate}
                  </h5>
                )}
                <SchedulesGroups
                  schedules={schedules!}
                  fullVersion={false}
                  highlightUserId={highlightUserId}
                  isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                />
              </div>
            ) : null}
            {resource ? (
              <>
                {resource.description ? (
                  <>
                    <div className="my-2 text-sm">
                      <Spoiler
                        maxHeight={175}
                        showLabel="Show more"
                        hideLabel="Hide"
                      >
                        <TextEditorContent value={resource.description} />
                      </Spoiler>
                    </div>
                  </>
                ) : null}
                <div className="mt-1 flex max-w-[300px] flex-wrap gap-1">
                  {showAvailabilityDetails &&
                    handleUnavailableDeskDetails(authenticatedUser, resource)}
                  {!resource.is_open &&
                  handleDeskAssigned(
                    authenticatedUser,
                    resourceWithSchedules
                  ) ? (
                    <span className={classes.StatusBadge}>
                      <svg
                        className="h-1.5 w-1.5 fill-yellow-500"
                        viewBox="0 0 6 6"
                        aria-hidden="true"
                      >
                        <circle cx={3} cy={3} r={3} />
                      </svg>
                      Assigned
                    </span>
                  ) : null}
                  {resource.is_approvable ? (
                    <span className={classes.StatusBadge}>
                      <svg
                        className="h-1.5 w-1.5 fill-yellow-500"
                        viewBox="0 0 6 6"
                        aria-hidden="true"
                      >
                        <circle cx={3} cy={3} r={3} />
                      </svg>
                      Requires Approval
                    </span>
                  ) : null}
                  {amenities && (
                    <div className={"flex flex-wrap gap-1"}>
                      {amenities.map((amenity) => (
                        <span
                          key={amenity.id}
                          className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-100 px-2 py-1 font-medium text-gray-900 text-xs ring-1 ring-gray-200 ring-inset"
                        >
                          {amenity.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {resource.total_slots &&
                  handleDeskCapacity(authenticatedUser, resource) ? (
                    <span className={classes.StatusBadge}>
                      <svg
                        className="h-1.5 w-1.5 fill-blue-500"
                        viewBox="0 0 6 6"
                        aria-hidden="true"
                      >
                        <circle cx={3} cy={3} r={3} />
                      </svg>
                      Space for {resource.total_slots}
                    </span>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
